import { ReactNode, useSyncExternalStore } from "react";

// Module-level store so ALL admin pages share one search query,
// regardless of where in the tree they render <AdminShell>.
// (Previously used React context wrapped inside AdminShell, which meant
// the page component - which mounts the shell - was OUTSIDE the provider
// and always read the fallback empty query.)
let _query = "";
const listeners = new Set<() => void>();

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot() {
  return _query;
}

function getServerSnapshot() {
  return "";
}

function emit() {
  listeners.forEach((l) => l());
}

export function setAdminSearchQuery(q: string) {
  if (q === _query) return;
  _query = q;
  emit();
}

export function AdminSearchProvider({ children }: { children: ReactNode }) {
  // Kept for backwards compat with existing imports; state is module-level.
  return <>{children}</>;
}

export function useAdminSearch(): {
  query: string;
  setQuery: (q: string) => void;
  clear: () => void;
} {
  const query = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    query,
    setQuery: setAdminSearchQuery,
    clear: () => setAdminSearchQuery(""),
  };
}

/** Case-insensitive substring match across the given fields. */
export function matchesQuery(query: string, ...fields: Array<string | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => (f ?? "").toLowerCase().includes(q));
}

/**
 * Filters an array of student records to show ONLY
 * successfully paid students (`status === 'paid'`).
 */
export function filterPaidStudentsOnly<T extends { status?: string | null; payment_status?: string | null }>(
  items: T[],
): T[] {
  return items.filter((item) => item.status?.toLowerCase() === "paid" || item.payment_status?.toLowerCase() === "paid");
}

/** Badge styling & label props for rendering the Paid Badge */
export const PAID_BADGE_PROPS = {
  label: "PAID",
  className:
    "bg-emerald-600/10 text-emerald-600 border border-emerald-600/20 font-bold text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1",
} as const;
